import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, useEffect, useRef } from 'react';
import ChangeProductIcon from 'src/components/pages/userInterfaceSettings/icons/change-product.svg';
import { T } from 'src/shared/helpers';
import FolderIcon from 'src/shared/icons/folder-solid.svg';
import HomeIcon from 'src/shared/icons/home-solid.svg';
import MagicIcon from 'src/shared/icons/magic-solid.svg';
import CloseIcon from 'src/shared/icons/times-solid.svg';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled from 'styled-components';
import { ToolConfig, ToolType } from '../../types';
import { Carousel } from '../presets/carousel';

const LogoWrapper = styled.div`
    position: absolute;
    left: 24px;
    display: flex;
    align-items: center;

    svg {
        height: 24px;
        width: auto;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 12px 24px;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    min-height: 64px;
`;

const ToolsCarouselWrapper = styled(Carousel)`
    max-width: calc(100% - 780px);
`;

const ToolButton = styled.button<{ $active: boolean; $disabled?: boolean }>`
    padding: 10px 20px;
    border: none;
    border-radius: 18px;
    background: ${(props) => (props.$active ? '#000000' : 'transparent')};
    color: ${(props) => (props.$active ? '#ffffff' : '#000000')};
    font-size: 13px;
    font-weight: 500;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    transition:
        background 0.2s ease,
        color 0.2s ease,
        opacity 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => (props.$disabled ? (props.$active ? '#000000' : 'transparent') : props.$active ? '#000000' : '#e5e7eb')};
    }
`;

const CloseButton = styled.button`
    position: absolute;
    right: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #000000;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: #333333;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 0;
        fill: currentColor;
    }
`;

const HeaderActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    border-radius: 9999px;
    background: #f3f5f6;
    color: #000000;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: black;
        color: white;
    }
`;

const HeaderIconButton = styled(HeaderActionButton)`
    width: 36px;
    height: 36px;
    padding: 0;

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

const CustomizerActions = styled.div`
    position: absolute;
    left: 150px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LeftSwitchWrapper = styled.div`
    position: absolute;
    left: 150px;
    display: flex;
    align-items: center;
    background: #f3f5f6;
    border-radius: 9999px;
    padding: 4px;
`;

const LeftSwitchSlider = styled.div<{ $position: 'left' | 'right' }>`
    position: absolute;
    top: 4px;
    left: ${(props) => (props.$position === 'left' ? '4px' : '50%')};
    width: calc(50% - 4px);
    height: calc(100% - 8px);
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    border-radius: 9999px;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 0;
`;

const HomeButton = styled(HeaderIconButton)<{ $disabled?: boolean }>`
    position: absolute;
    left: 360px;
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    transition:
        background 0.2s ease,
        color 0.2s ease,
        opacity 0.2s ease;
`;

const LeftSwitchOption = styled.button<{ $active: boolean; $disabled?: boolean }>`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    padding: 5px 12px;
    border: none;
    border-radius: 9999px;
    background: transparent;
    color: ${(props) => (props.$active ? '#ffffff' : '#000000')};
    font-size: 13px;
    font-weight: 500;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    transition:
        color 0.3s ease,
        opacity 0.2s ease;
    white-space: nowrap;

    &:hover {
        color: ${(props) => (props.$active ? '#ffffff' : '#374151')};
    }

    > * {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

interface LandingTab {
    id: string;
    name: string;
    active: boolean;
    onClick: () => void;
}

interface HeaderProps {
    tools: ToolConfig[];
    /** Selected tool ID, or null for homepage mode */
    selectedTool: ToolType | null;
    onToolChange: (toolId: ToolType) => void;
    onClose?: () => void;
    onGoHome?: () => void;
    onShowLibrary?: () => void;
    isLibraryActive?: boolean;
    isIframeMode?: boolean;
    onSwitchProduct?: () => void;
    /** Whether navigation is disabled (e.g. during auto-capture) */
    isNavigationDisabled?: boolean;
    /** Tabs to show in the header when a homepage landing overlay is open */
    landingTabs?: LandingTab[];
}

export const Header: FC<HeaderProps> = ({
    tools,
    selectedTool,
    onToolChange,
    onClose,
    onGoHome,
    onShowLibrary,
    isLibraryActive,
    isIframeMode,
    onSwitchProduct,
    isNavigationDisabled = false,
    landingTabs
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const isHomepage = selectedTool === null;

    // Scroll the active tab into view when it changes — works for both
    // workspace mode (selectedTool) and homepage overlay mode (landingTabs).
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        let activeIndex = -1;
        if (selectedTool) {
            activeIndex = tools.findIndex((t) => t.id === selectedTool);
        } else if (landingTabs && landingTabs.length > 0) {
            activeIndex = landingTabs.findIndex((t) => t.active);
        }
        if (activeIndex < 0) return;

        const buttons = container.querySelectorAll('button');
        const activeButton = buttons[activeIndex];

        if (activeButton) {
            activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [selectedTool, tools, landingTabs]);

    const handleToolsClick = () => {
        if (isNavigationDisabled) return;
        if (isLibraryActive && onShowLibrary) {
            onShowLibrary();
        }
    };

    const handleLibraryClick = () => {
        if (isNavigationDisabled) return;
        if (!isLibraryActive && onShowLibrary) {
            onShowLibrary();
        }
    };

    return (
        <HeaderContainer>
            <LogoWrapper>
                <LogoZakeke />
            </LogoWrapper>
            {isIframeMode && (
                <CustomizerActions>
                    <ZTooltip position='bottom' title={T._('Switch product', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                        <HeaderIconButton onClick={onSwitchProduct}>
                            <Icon>
                                <ChangeProductIcon />
                            </Icon>
                        </HeaderIconButton>
                    </ZTooltip>
                </CustomizerActions>
            )}
            {!isIframeMode && onShowLibrary && (
                <LeftSwitchWrapper>
                    <LeftSwitchSlider $position={isLibraryActive ? 'right' : 'left'} />
                    <LeftSwitchOption $active={!isLibraryActive} $disabled={isNavigationDisabled} onClick={handleToolsClick}>
                        <Icon>
                            <MagicIcon />
                        </Icon>
                        {T._('Tools', 'Admin')}
                    </LeftSwitchOption>
                    <LeftSwitchOption $active={!!isLibraryActive} $disabled={isNavigationDisabled} onClick={handleLibraryClick}>
                        <Icon>
                            <FolderIcon />
                        </Icon>
                        {T._('Library', 'Admin')}
                    </LeftSwitchOption>
                </LeftSwitchWrapper>
            )}
            {!isIframeMode && !isLibraryActive && onGoHome && (!isHomepage || (landingTabs && landingTabs.length > 0)) && (
                <HomeButton
                    $disabled={isNavigationDisabled}
                    onClick={isNavigationDisabled ? undefined : onGoHome}
                    aria-label={T._('Home', 'Admin')}
                    title={T._('Return to tools list', 'Admin')}
                >
                    <Icon>
                        <HomeIcon />
                    </Icon>
                </HomeButton>
            )}
            {!isIframeMode && !isHomepage && !isLibraryActive && (
                <ToolsCarouselWrapper scrollRef={scrollRef} scrollAmount={150}>
                    {tools.map((tool) => (
                        <ToolButton
                            key={tool.id}
                            $active={selectedTool === tool.id}
                            $disabled={isNavigationDisabled}
                            onClick={isNavigationDisabled ? undefined : () => onToolChange(tool.id)}
                        >
                            {T._(tool.name, 'Admin')}
                        </ToolButton>
                    ))}
                </ToolsCarouselWrapper>
            )}
            {!isIframeMode && isHomepage && !isLibraryActive && landingTabs && landingTabs.length > 0 && (
                <ToolsCarouselWrapper scrollRef={scrollRef} scrollAmount={150}>
                    {landingTabs.map((tab) => (
                        <ToolButton key={tab.id} $active={tab.active} onClick={tab.onClick}>
                            {T._(tab.name, 'Admin')}
                        </ToolButton>
                    ))}
                </ToolsCarouselWrapper>
            )}
            {onClose && (
                <CloseButton onClick={onClose} aria-label={T._('Close', 'Admin')}>
                    <Icon>
                        <CloseIcon />
                    </Icon>
                </CloseButton>
            )}
        </HeaderContainer>
    );
};

export default Header;
